/**
 * GoHighLevel CRM Integration — Full UI
 *
 * Tabs:
 *   1. Contacts       — full list, search, sync from AcquireOS deal
 *   2. Pipeline Board — Kanban view of GHL opportunities by stage
 *   3. Conversations  — SMS/email thread feed
 *   4. Automations    — Workflow status + webhook event log
 *   5. Settings       — Enter/update GHL API Key, Location ID, Pipeline ID
 *
 * Configure credentials in .env (VITE_GHL_API_KEY / VITE_GHL_LOCATION_ID)
 * OR directly in the Settings tab in the app.
 */

import { useEffect, useState } from 'react';
import useGhlStore from '../store/ghlStore';
import useDealStore from '../store/dealStore';
import useAuthStore from '../store/authStore';
import '../styles/crm.css';

// ── Stage color palette ───────────────────────────────────────────────────────
const STAGE_COLORS = {
  Lead:             { bg: '#dbeafe', border: '#93c5fd', text: '#1d4ed8' },
  Qualified:        { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  'Call Scheduled': { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
  Negotiation:      { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
  'Under Contract': { bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
  Closed:           { bg: '#dcfce7', border: '#86efac', text: '#14532d' },
};
const DEFAULT_STAGE_COLOR = { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569' };

function stageColor(name) {
  return STAGE_COLORS[name] || DEFAULT_STAGE_COLOR;
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ configured }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: configured ? '#dcfce7' : '#fef2f2',
      color: configured ? '#16a34a' : '#dc2626',
      border: `1.5px solid ${configured ? '#86efac' : '#fecaca'}`,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: configured ? '#16a34a' : '#dc2626', display: 'inline-block' }} />
      {configured ? 'GHL Connected' : 'GHL Not Configured'}
    </div>
  );
}

// ── Contacts Tab ──────────────────────────────────────────────────────────────
function ContactsTab({ contacts, isLoading, onRefresh, onSyncDeal }) {
  const [search, setSearch] = useState('');
  const filtered = contacts.filter(c => {
    const q = search.toLowerCase();
    return !q || `${c.firstName} ${c.lastName} ${c.email} ${c.phone}`.toLowerCase().includes(q);
  });

  return (
    <div>
      <div className="crm-section-header">
        <div>
          <h3 className="crm-section-title">Contacts</h3>
          <p className="crm-section-sub">{contacts.length} contacts synced from M2M</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="crm-search"
            placeholder="Search name, email, phone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="crm-btn-outline" onClick={onRefresh}>↻ Refresh</button>
          {onSyncDeal && (
            <button className="crm-btn-primary" onClick={onSyncDeal}>
              ＋ Sync Current Deal
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="crm-empty">Loading contacts from GoHighLevel…</div>
      ) : filtered.length === 0 ? (
        <div className="crm-empty">
          {contacts.length === 0
            ? '⚙️ No contacts yet. Make sure your GHL credentials are configured in the Settings tab.'
            : 'No contacts match your search.'}
        </div>
      ) : (
        <div className="crm-contacts-grid">
          {filtered.slice(0, 100).map((c, i) => (
            <div key={c.id || i} className="crm-contact-card">
              <div className="crm-contact-avatar">
                {(c.firstName || c.email || 'C').charAt(0).toUpperCase()}
              </div>
              <div className="crm-contact-info">
                <div className="crm-contact-name">
                  {[c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed Contact'}
                </div>
                {c.email && <div className="crm-contact-email">{c.email}</div>}
                {c.phone && <div className="crm-contact-phone">{c.phone}</div>}
                {c.tags?.length > 0 && (
                  <div className="crm-tags">
                    {c.tags.slice(0, 3).map(t => (
                      <span key={t} className="crm-tag">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pipeline Kanban ───────────────────────────────────────────────────────────
function PipelineTab({ opportunities, stages, isLoading, onRefresh }) {
  const stageNames = stages.length > 0
    ? stages.map(s => s.name)
    : ['Lead', 'Qualified', 'Call Scheduled', 'Negotiation', 'Under Contract', 'Closed'];

  const stageMap = {};
  stageNames.forEach(n => { stageMap[n] = []; });
  opportunities.forEach(opp => {
    const sn = opp.stage?.name || opp.stageName || stageNames[0];
    if (!stageMap[sn]) stageMap[sn] = [];
    stageMap[sn].push(opp);
  });

  const totalValue = opportunities.reduce((s, o) => s + (Number(o.monetaryValue) || 0), 0);

  return (
    <div>
      <div className="crm-section-header">
        <div>
          <h3 className="crm-section-title">Pipeline Board</h3>
          <p className="crm-section-sub">
            {opportunities.length} opportunities · Total value: ${totalValue.toLocaleString()}
          </p>
        </div>
        <button className="crm-btn-outline" onClick={onRefresh}>↻ Refresh</button>
      </div>

      {isLoading ? (
        <div className="crm-empty">Loading pipeline from M2M…</div>
      ) : (
        <div className="crm-kanban">
          {stageNames.map(stage => {
            const col = stageColor(stage);
            const cards = stageMap[stage] || [];
            const colValue = cards.reduce((s, o) => s + (Number(o.monetaryValue) || 0), 0);
            return (
              <div key={stage} className="crm-kanban-col">
                <div className="crm-kanban-header" style={{ background: col.bg, borderBottom: `2px solid ${col.border}` }}>
                  <span style={{ fontWeight: 800, fontSize: 13, color: col.text }}>{stage}</span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {colValue > 0 && <span style={{ fontSize: 11, color: col.text, opacity: 0.8 }}>${(colValue / 1000).toFixed(0)}k</span>}
                    <span className="crm-kanban-count">{cards.length}</span>
                  </div>
                </div>
                <div className="crm-kanban-body">
                  {cards.map((opp, i) => (
                    <div key={opp.id || i} className="crm-kanban-card">
                      <div className="crm-kanban-card-name">{opp.name || opp.contactName || 'Opportunity'}</div>
                      {opp.monetaryValue > 0 && (
                        <div className="crm-kanban-card-value">${Number(opp.monetaryValue).toLocaleString()}</div>
                      )}
                      {opp.assignedTo && (
                        <div className="crm-kanban-card-assignee">👤 {opp.assignedTo}</div>
                      )}
                    </div>
                  ))}
                  {cards.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 12, padding: '20px 0' }}>
                      No opportunities
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Conversations Tab ─────────────────────────────────────────────────────────
function ConversationsTab({ conversations, isLoading, onRefresh }) {
  return (
    <div>
      <div className="crm-section-header">
        <div>
          <h3 className="crm-section-title">Conversation Feed</h3>
          <p className="crm-section-sub">{conversations.length} recent conversations from M2M</p>
        </div>
        <button className="crm-btn-outline" onClick={onRefresh}>↻ Refresh</button>
      </div>

      {isLoading ? (
        <div className="crm-empty">Loading conversations…</div>
      ) : conversations.length === 0 ? (
        <div className="crm-empty">
          💬 No conversations yet. GHL SMS / email activity will appear here once configured.
        </div>
      ) : (
        <div className="crm-conversations">
          {conversations.map((c, i) => (
            <div key={c.id || i} className="crm-conversation-item">
              <div className="crm-convo-avatar">
                {(c.contactName || c.type || 'C').charAt(0).toUpperCase()}
              </div>
              <div className="crm-convo-body">
                <div className="crm-convo-name">{c.contactName || 'Unknown Contact'}</div>
                <div className="crm-convo-preview">{c.lastMessage || c.body || 'No message preview'}</div>
              </div>
              <div className="crm-convo-meta">
                <div className="crm-convo-type">{c.type || 'Message'}</div>
                <div className="crm-convo-time">
                  {c.dateUpdated ? new Date(c.dateUpdated).toLocaleDateString() : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Automations Tab ───────────────────────────────────────────────────────────
function AutomationsTab({ automations, webhookLog, isConfigured }) {
  const statusCards = [
    {
      label: 'M2M Connection',
      icon: '🔗',
      active: isConfigured,
      desc: isConfigured ? 'API key and location connected' : 'Configure in Settings tab',
    },
    {
      label: 'Contact Sync',
      icon: '👥',
      active: isConfigured,
      desc: 'Triggers on AcquireOS lead capture',
    },
    {
      label: 'Stage Updates',
      icon: '📋',
      active: isConfigured,
      desc: 'Syncs opportunity stage on pipeline advance',
    },
    {
      label: 'Webhook Listener',
      icon: '⚡',
      active: webhookLog.length > 0,
      desc: `${webhookLog.length} events received`,
    },
  ];

  return (
    <div>
      <div className="crm-section-header">
        <div>
          <h3 className="crm-section-title">Automation Status</h3>
          <p className="crm-section-sub">Real-time automation and webhook monitoring</p>
        </div>
      </div>

      <div className="crm-status-grid">
        {statusCards.map(card => (
          <div key={card.label} className={`crm-status-card ${card.active ? 'active' : ''}`}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{card.icon}</div>
            <div className="crm-status-dot-row">
              <span className={`crm-status-dot ${card.active ? 'active' : ''}`} />
              <span className="crm-status-label">{card.label}</span>
            </div>
            <div className="crm-status-desc">{card.desc}</div>
          </div>
        ))}
      </div>

      {automations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h4 className="crm-subsection-title">Active M2M Workflows ({automations.length})</h4>
          <div className="crm-workflow-list">
            {automations.map((wf, i) => (
              <div key={wf.id || i} className="crm-workflow-item">
                <span className={`crm-status-dot ${wf.status === 'published' ? 'active' : ''}`} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>{wf.name}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>{wf.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {webhookLog.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h4 className="crm-subsection-title">Recent Webhook Events</h4>
          <div className="crm-webhook-log">
            {webhookLog.slice(0, 15).map((evt, i) => (
              <div key={i} className="crm-webhook-item">
                <span className="crm-webhook-type">{evt.type || 'event'}</span>
                <span className="crm-webhook-time">
                  {evt.receivedAt ? new Date(evt.receivedAt).toLocaleTimeString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab({ onSaved }) {
  const { config, setConfig, clearCredentials, isConfigured } = useGhlStore();
  const [form, setForm] = useState({
    apiKey:     config.apiKey     || '',
    locationId: config.locationId || '',
    pipelineId: config.pipelineId || '',
  });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setConfig(form);
    setSaved(true);
    setTimeout(() => { setSaved(false); onSaved && onSaved(); }, 2000);
  };

  const handleClear = () => {
    clearCredentials();
    setForm({ apiKey: '', locationId: '', pipelineId: '' });
  };

  return (
    <div>
      <div className="crm-section-header">
        <div>
          <h3 className="crm-section-title">M2M Settings</h3>
          <p className="crm-section-sub">
            Enter your M2M credentials below. These are stored locally on your device.
          </p>
        </div>
        <StatusBadge configured={isConfigured} />
      </div>

      <div className="crm-settings-card">
        <div className="crm-settings-notice">
          <div style={{ fontSize: 20, marginBottom: 8 }}>🔑</div>
          <h4 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: 15, fontWeight: 700 }}>
            How to get your M2M credentials
          </h4>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
            <li>Log in to your <strong>M2M account</strong> at <a href="https://app.gohighlevel.com" target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>app.gohighlevel.com</a></li>
            <li>Go to <strong>Settings → Business Info</strong> and copy your <strong>Location ID</strong></li>
            <li>Go to <strong>Settings → Integrations → API Keys</strong> and generate a new <strong>API Key</strong></li>
            <li>Optionally, go to <strong>Opportunities → Pipelines</strong> and copy the <strong>Pipeline ID</strong> from the URL</li>
          </ol>
        </div>

        <div className="crm-settings-form">
          <div className="crm-settings-field">
            <label className="crm-settings-label">
              M2M API Key <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              className="crm-settings-input"
              type="password"
              placeholder="REPLACE_WITH_YOUR_GHL_API_KEY"
              value={form.apiKey}
              onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
            />
            <span className="crm-settings-hint">Found under Settings → Integrations → API Keys</span>
          </div>

          <div className="crm-settings-field">
            <label className="crm-settings-label">
              Location ID <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              className="crm-settings-input"
              type="text"
              placeholder="REPLACE_WITH_YOUR_GHL_LOCATION_ID"
              value={form.locationId}
              onChange={e => setForm(f => ({ ...f, locationId: e.target.value }))}
            />
            <span className="crm-settings-hint">Found under Settings → Business Info</span>
          </div>

          <div className="crm-settings-field">
            <label className="crm-settings-label">Pipeline ID <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
            <input
              className="crm-settings-input"
              type="text"
              placeholder="REPLACE_WITH_YOUR_GHL_PIPELINE_ID"
              value={form.pipelineId}
              onChange={e => setForm(f => ({ ...f, pipelineId: e.target.value }))}
            />
            <span className="crm-settings-hint">Visible in the URL when viewing a pipeline in M2M. Used to scope contacts to a specific pipeline.</span>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button
              className="crm-btn-primary"
              onClick={handleSave}
              disabled={!form.apiKey || !form.locationId}
            >
              {saved ? '✓ Saved!' : '💾 Save & Connect'}
            </button>
            {isConfigured && (
              <button className="crm-btn-danger" onClick={handleClear}>
                🗑 Disconnect M2M
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="crm-settings-env-hint">
        <strong>💡 Pro Tip (Developers):</strong> You can also set these in <code>frontend/.env</code>:
        <pre className="crm-env-block">{`VITE_GHL_API_KEY=REPLACE_WITH_YOUR_GHL_API_KEY
VITE_GHL_LOCATION_ID=REPLACE_WITH_YOUR_GHL_LOCATION_ID
VITE_GHL_PIPELINE_ID=REPLACE_WITH_YOUR_GHL_PIPELINE_ID`}</pre>
      </div>
    </div>
  );
}

// ── Main CRM Page ─────────────────────────────────────────────────────────────
export default function CRMPage() {
  const { dealState } = useDealStore();
  const { user } = useAuthStore();
  const {
    contacts, opportunities, stages, conversations, automations, webhookLog,
    isLoading, error, isConfigured,
    loadContacts, loadPipelines, loadOpportunities, loadConversations, loadAutomations,
    syncContactToGhl,
  } = useGhlStore();

  const [activeTab, setActiveTab] = useState('contacts');

  useEffect(() => {
    if (isConfigured) {
      loadContacts();
      loadPipelines().then(() => loadOpportunities());
      loadConversations();
      loadAutomations && loadAutomations();
    }
  }, [isConfigured]);

  const handleSyncDeal = async () => {
    if (!dealState?.dealId) return;
    const summary = dealState;
    await syncContactToGhl({
      name: user?.name || '',
      email: user?.email || '',
      phone: '',
      tags: ['AcquireOS', 'Deal'],
    });
    loadContacts();
  };

  const TABS = [
    { id: 'contacts',     label: '👥 Contacts' },
    { id: 'pipeline',     label: '📋 Pipeline' },
    { id: 'conversations',label: '💬 Conversations' },
    { id: 'automations',  label: '⚡ Automations' },
    { id: 'settings',     label: '⚙️ Settings' },
  ];

  return (
    <div className="crm-page">
      {/* Page Header */}
      <div className="crm-header">
        <div className="crm-header-brand">
          <div className="crm-header-eyebrow">CRM Integration</div>
          <h1 className="crm-header-title">M2M CRM</h1>
          <p className="crm-header-sub">
            Contacts · Pipeline Board · Conversations · Automations — powered by M2M API
          </p>
        </div>
        <div className="crm-header-actions">
          <StatusBadge configured={isConfigured} />
          {!isConfigured && (
            <button className="crm-btn-primary" onClick={() => setActiveTab('settings')}>
              ⚙️ Configure M2M →
            </button>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="crm-error-banner">
          ⚠️ {error}
          <button style={{ marginLeft: 12, fontWeight: 700, background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer' }}
            onClick={() => setActiveTab('settings')}>
            Fix in Settings →
          </button>
        </div>
      )}

      {/* Not-configured banner */}
      {!isConfigured && (
        <div className="crm-config-banner">
          <div style={{ fontSize: 32, marginBottom: 8 }}>🔗</div>
          <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: 16, fontWeight: 700 }}>
            M2M Not Connected
          </h3>
          <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
            Enter your M2M API Key and Location ID in the Settings tab to start syncing contacts, pipelines, and conversations.
          </p>
          <button className="crm-btn-primary" onClick={() => setActiveTab('settings')}>
            ⚙️ Go to Settings
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="crm-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`crm-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="crm-tab-panel">
        {activeTab === 'contacts' && (
          <ContactsTab
            contacts={contacts}
            isLoading={isLoading}
            onRefresh={loadContacts}
            onSyncDeal={dealState?.dealId ? handleSyncDeal : null}
          />
        )}
        {activeTab === 'pipeline' && (
          <PipelineTab
            opportunities={opportunities}
            stages={stages}
            isLoading={isLoading}
            onRefresh={() => loadPipelines().then(() => loadOpportunities())}
          />
        )}
        {activeTab === 'conversations' && (
          <ConversationsTab
            conversations={conversations}
            isLoading={isLoading}
            onRefresh={loadConversations}
          />
        )}
        {activeTab === 'automations' && (
          <AutomationsTab
            automations={automations}
            webhookLog={webhookLog}
            isConfigured={isConfigured}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsTab
            onSaved={() => {
              loadContacts();
              loadPipelines().then(() => loadOpportunities());
            }}
          />
        )}
      </div>
    </div>
  );
}
